'use client';

import { Card } from '@/components/card';
import { GRADIENT_CTA_CLASS } from '@/components/free-tools/brand';
import { TrackedPseoLink } from '@/components/pseo/tracked-pseo-link';
import type { PseoCtaSurface } from '@/components/pseo/track-pseo-cta';
import { usePaidAttribution } from '@/lib/analytics/use-paid-attribution';
import { plans } from '@/lib/pricing-data';
import { cn } from '@/lib/utils';

/**
 * The conversion block for pSEO pages. Replaces the small gradient text-button
 * card that sat once at the top of every comparison page. Rendered up to three
 * times per page (hero, mid-body, final) with a distinct `surface` so the
 * `pseo_cta_clicked` event tells us which position earns the click.
 *
 * Client component, but the DEFAULT render is what SSR and crawlers see — the
 * pages stay static/ISR (never read `cookies()` server-side in this tree).
 * After hydration, a PAID-attributed visitor (ad click — see
 * usePaidAttribution) gets the annual Pro checkout as the PRIMARY CTA with the
 * free trial demoted to secondary (founder decision 2026-09-01: annual prepay
 * compresses CAC payback to day one — the prerequisite for first-page bidding,
 * see project_docs/marketing/google-ads/TRACKER.md). `checkout=1` makes
 * /pricing resume straight into the annual Polar checkout after sign-up.
 *
 * Honesty guardrails: the $/mo figure always sits next to the billed-yearly
 * total; guarantee wording matches the ToS (30-day money-back, 2026-08-25); no
 * countdown or pressure copy. Every claim in the paid variant is true for all
 * visitors — personalization by arrival, not cloaking (seo-bible Ch. 2.8).
 */
export interface PseoCtaBandProps {
  slug: string;
  surface: PseoCtaSurface;
  headline?: string;
  subcopy?: string;
  primary?: { href: string; label: string };
  secondary?: { href: string; label: string };
  /** Compact variant for mid-body placement. */
  compact?: boolean;
  className?: string;
}

const DEFAULT_PRIMARY = { href: '/sign-up', label: 'Try Wisprs free' };

const DEFAULT_SECONDARY = { href: '/pricing', label: 'See pricing' };

const TRUST_LINE = '30 minutes a day free. No credit card. Cancel anytime.';

const pro = plans.find((p) => p.id === 'pro');

/** $20 — per-month rate on annual billing, from pricing-data (never hardcode). */
const PRO_ANNUAL_PER_MONTH = pro?.annualPrice ?? 20;

/** $240 — the actual yearly charge, shown right next to the $/mo figure. */
const PRO_ANNUAL_TOTAL = PRO_ANNUAL_PER_MONTH * 12;

export function PseoCtaBand({
  slug,
  surface,
  headline = 'Upload one file and judge for yourself',
  subcopy = 'Clean transcripts with speaker labels in minutes. Export TXT, SRT, VTT, DOCX. 100+ languages.',
  primary: primaryProp = DEFAULT_PRIMARY,
  secondary: secondaryProp = DEFAULT_SECONDARY,
  compact = false,
  className,
}: PseoCtaBandProps) {
  const { isPaid } = usePaidAttribution();

  // Post-hydration paid-visitor variant: annual Pro checkout first, free trial
  // second. Never rendered on the server → crawler HTML is the default above.
  const { primary, secondary, trustLine } = isPaid
    ? {
        primary: {
          href: `/pricing?plan=pro&interval=annual&checkout=1&from=ads_landing_${surface}`,
          label: `Get Pro · $${PRO_ANNUAL_PER_MONTH}/mo billed annually`,
        },
        secondary: { href: '/sign-up', label: 'or try free first' },
        trustLine: `$${PRO_ANNUAL_TOTAL} billed yearly · 30-day money-back guarantee · Cancel anytime`,
      }
    : { primary: primaryProp, secondary: secondaryProp, trustLine: TRUST_LINE };

  return (
    <Card
      variant="elevated"
      className={cn(compact ? 'my-10 p-5 sm:p-6' : 'my-10 p-6 sm:p-8', className)}
    >
      <div
        className={cn(
          'flex flex-col gap-5',
          compact ? 'sm:flex-row sm:items-center sm:justify-between' : ''
        )}
      >
        <div className="space-y-2">
          <p
            className={cn(
              'font-semibold text-[rgb(var(--foreground))]',
              compact ? 'text-lg' : 'text-xl sm:text-2xl'
            )}
          >
            {headline}
          </p>
          <p className="max-w-2xl text-sm leading-relaxed text-[rgb(var(--foreground-secondary))]">
            {subcopy}
          </p>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:shrink-0">
          <TrackedPseoLink
            href={primary.href}
            surface={surface}
            slug={slug}
            label={primary.label}
            className={cn(GRADIENT_CTA_CLASS, 'w-full whitespace-nowrap border border-transparent sm:w-auto sm:shrink-0', compact ? 'px-6 py-3 text-sm' : '')}
          >
            {primary.label}
          </TrackedPseoLink>
          <TrackedPseoLink
            href={secondary.href}
            surface={surface}
            slug={slug}
            label={secondary.label}
            className={cn(
              'inline-flex w-full items-center justify-center whitespace-nowrap rounded-full border border-[rgb(var(--surface-border))] text-center font-semibold text-[rgb(var(--foreground))] transition-colors hover:bg-white/5 sm:w-auto sm:shrink-0',
              compact ? 'px-6 py-3 text-sm' : 'px-8 py-4 text-base'
            )}
          >
            {secondary.label}
          </TrackedPseoLink>
        </div>
      </div>
      <p className="mt-4 text-xs text-[rgb(var(--foreground-tertiary))]">{trustLine}</p>
    </Card>
  );
}
